import {
	DpModernProductCard,
	DpModernProductCardVariant,
} from '@/components/blocks/DpModernProductCard';
import { CouponTicketIcon } from '@/components/blocks/DpRepurchaseCoupon/parts/CouponIcons';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { applyRepurchaseDiscount } from '@/data/Content/RepurchaseCouponState';
import { RepurchasePricing, useRepurchasePricing } from '@/data/context/RepurchasePricing';
import { ProductTypeWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import { getImage } from '@/utils/getImage';
import { useProductExtractMerchandisingAssociationsWithInfoElectronico } from '@/utils/productExtractMerchandisingAssociations';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { formatPrice } from 'integration/utils/formatPrice';
import { FC, useMemo, useState } from 'react';

const getRepurchasePrices = (
	pricing: RepurchasePricing | undefined,
	basePrice: number,
	products: ProductTypeWithInfoElectronico[]
) =>
	pricing && basePrice > 0 && products.every((item) => !pricing.isExcluded(item))
		? {
				price: basePrice,
				discount: applyRepurchaseDiscount(basePrice, pricing.percentage),
				note: `${pricing.percentage}% OFF aplicado`,
		  }
		: undefined;

export const ProductCardModern: FC<{
	product: ProductTypeWithInfoElectronico;
	variant?: DpModernProductCardVariant;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	onClick?: () => void;
}> = ({ product: _product, clickSource, variant, onClick }) => {
	const { data: productVariantColors } =
		useProductExtractMerchandisingAssociationsWithInfoElectronico(_product);
	const orderedVariantColors = useMemo(() => {
		const base = productVariantColors.filter(
			(colorVariant) => colorVariant.partNumber === _product.partNumber
		);
		const rest = productVariantColors.filter(
			(colorVariant) => colorVariant.partNumber !== _product.partNumber
		);
		return [...base, ...rest];
	}, [productVariantColors, _product.partNumber]);
	const [product, setProduct] = useState(_product);
	const repurchasePricing = useRepurchasePricing();

	const { price, priceWithoutOffer, shouldOffer } = calculateProductPrices({
		list: product.productPrice.list || 0,
		offer: product.productPrice.offer || 0,
		ribbons: product?.ribbons ?? [],
	});

	const category = useMemo(() => {
		const family = findProductAttribute(product.attributes, 'Familia');
		const gender = findProductAttribute(product.attributes, 'Genero');
		const uso = findProductAttribute(product.attributes, 'Uso');
		const brand = findProductAttribute(product.attributes, 'Marca');

		if (uso?.value === 'Electronicos' && brand) {
			return `${brand.value}`;
		}

		if (family && gender) {
			return `${family.value} ${gender.value}`;
		}

		return product.manufacturer;
	}, [product.attributes, product.manufacturer]);

	const prices = useMemo(() => {
		if (product.infoElectronico) {
			return {
				discount: product.infoElectronico.strikedDeContado
					? product.infoElectronico.offerDeContado
					: undefined,
				price:
					product.infoElectronico.strikedDeContado || product.infoElectronico.offerDeContado || '',
				note: undefined,
			};
		}

		return (
			getRepurchasePrices(repurchasePricing, shouldOffer ? price : priceWithoutOffer, [
				_product,
				product,
			]) ?? {
				price: priceWithoutOffer,
				discount: shouldOffer ? price : undefined,
				note: undefined,
			}
		);
	}, [product, _product, price, priceWithoutOffer, shouldOffer, repurchasePricing]);

	return (
		<DpModernProductCard
			variant={variant}
			seo={product?.seo?.href}
			title={product.name}
			category={Array.isArray(category) ? category[0] : category ?? ''}
			image={getImage(product.thumbnail)}
			price={formatPrice(prices.price, 'mxn')}
			discount={prices.discount ? formatPrice(prices.discount, 'mxn') : undefined}
			priceNote={
				prices.note ? (
					<>
						<CouponTicketIcon width={18} height={16} />
						{prices.note}
					</>
				) : undefined
			}
			showPrice={product.showPrice}
			ribbons={product?.ribbons}
			variants={orderedVariantColors}
			partNumber={product.partNumber}
			onSelectColor={(selectedVariant) => setProduct(selectedVariant)}
			clickSource={clickSource}
			onClick={onClick}
		/>
	);
};
